import { useCallback, useEffect, useRef, useState } from 'react';
import { loadIceServers } from '../lib/iceServers';
import { io, Socket } from 'socket.io-client';
import { createE2eeSession, applySframe, sframeSupported, e2eeForced, type E2eeSession } from './sframe';
import { GuestCamController, attachGuestCamResponder, CAM_DATACHANNEL_LABEL, sendImageBytes, sendSignResult } from './guestCamResponder';
import type { Signer, SignMarker } from '@/lib/signTypes';
import { preferVideoCodec, tuneVideoSender, preferH264InSdp } from '../lib/codecPrefs';

/**
 * Welle 8d / Mesh M1 — Gast-/Experten-WebRTC-Client.
 *
 * Bewusst eigenständig und schlank, völlig getrennt vom Berater-/Stele-Stack:
 *  - Reiner ANSWERER: die etablierten Teilnehmer (Stele, Berater) offerieren dem
 *    Gast, der Gast antwortet. Pro eingehendem Offer wird lazy eine PeerConnection
 *    angelegt (keyed nach Absender-Socket-ID) — so skaliert es automatisch von
 *    einem Peer (nur Berater) auf zwei (Berater + Stele), ohne Sonderfälle.
 *  - Transport-verschlüsselt (DTLS-SRTP), KEIN SFrame auf den Gast-Strecken in M1.
 *    Das entspricht dem bestehenden „3+ Teilnehmer"-Verhalten. Pro-Strecke-SFrame
 *    folgt mit der Phase-2-Policy.
 *  - Bildschirm teilen via sender.replaceTrack (keine Neuverhandlung nötig).
 */

export interface RemotePeer {
  peerId: string;
  name?: string;
  role?: string;
  stream: MediaStream;
}

interface PeerMeta {
  name?: string;
  role?: string;
  guest?: boolean;
}

export interface GuestRtcState {
  status: 'idle' | 'connecting' | 'connected' | 'error' | 'ended';
  /** Welle 8d/M2: bei 'ended' — selbst aufgelegt vs. vom Berater/Call beendet. */
  endedReason: 'self' | 'remote' | null;
  localStream: MediaStream | null;
  peers: RemotePeer[];
  micOn: boolean;
  camOn: boolean;
  screenSharing: boolean;
  /** pre225: aktuell aktive Kamera-Richtung (für Spiegelung nur bei Frontkamera). */
  cameraFacing: 'user' | 'environment';
  /** pre219: vom Berater gesendetes PDF zur Unterschrift (öffnet das Signatur-Modal). */
  signRequest: { bytes: ArrayBuffer; name: string; sigId: string; signers?: Signer[]; markers?: SignMarker[]; requireIdent?: boolean; requireOtp?: boolean } | null;
  /** pre212: letzte Rückmeldung zum Bildschirmteilen (z. B. iOS-Hinweis). */
  screenShareError?: string | null;
  error: string | null;
  /** pre91: Ende-zu-Ende-Verschlüsselung (SFrame) gerade tatsächlich aktiv (nur Caller-/Gruppencall). */
  e2eeActive: boolean;
}

export function useGuestRtc(rtcToken: string | null, caller = false) {
  const callerRef = useRef(caller);
  callerRef.current = caller;
  const [state, setState] = useState<GuestRtcState>({
    status: 'idle',
    endedReason: null,
    localStream: null,
    peers: [],
    micOn: true,
    camOn: true,
    screenSharing: false,
    cameraFacing: 'user',
    signRequest: null,
    screenShareError: null,
    error: null,
    e2eeActive: false,
  });

  const socketRef = useRef<Socket | null>(null);
  const pcsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const pendingIceRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const metaRef = useRef<Map<string, PeerMeta>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const camCtrlRef = useRef<GuestCamController | null>(null);
  const facingRef = useRef<'user' | 'environment'>('user');
  const cycleCameraRef = useRef<(() => Promise<void>) | null>(null); // pre213: Geräte-Zyklus (Desktop-Mehrfachkameras)
  const camDcRef = useRef<RTCDataChannel | null>(null); // pre215: cam-ctrl-Kanal für Screenshot-Versand
  const wakeLockRef = useRef<any>(null); // pre216: Screen Wake Lock
  const signReqRef = useRef<{ bytes: ArrayBuffer; name: string; sigId: string } | null>(null); // pre219
  const applyCameraRef = useRef<((next: 'user' | 'environment') => Promise<void>) | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  // Welle 8d/M2: war die Verbindung je aufgebaut? → unterscheidet „Call beendet"
  // (Verabschiedung) von „kommt nie zustande" (Fehler/Warteraum).
  const wasConnectedRef = useRef(false);
  // pre91: SFrame nur im Caller-Modus (Gruppencall, 2 Parteien). Experten-Strecken bleiben
  // transport-only, da der Berater-guestPc dort kein SFrame anwendet.
  const e2eeRef = useRef<E2eeSession | null>(null);
  const e2eeIntentRef = useRef(false);

  const publishPeers = useCallback(() => {
    const peers: RemotePeer[] = [];
    pcsRef.current.forEach((pc, peerId) => {
      const stream = (pc as any).__remoteStream as MediaStream | undefined;
      if (stream) {
        const meta = metaRef.current.get(peerId);
        peers.push({ peerId, name: meta?.name, role: meta?.role, stream });
      }
    });
    setState((s) => ({ ...s, peers }));
  }, []);

  // ── Gemeinsamer PeerConnection-Aufbau ─────────────────────────────────────
  const ensurePc = useCallback((from: string): RTCPeerConnection => {
    const existing = pcsRef.current.get(from);
    if (existing) return existing;
    const pcConfig: RTCConfiguration = {
      iceServers: (window as any).__iceServers || [{ urls: 'stun:stun.l.google.com:19302' }],
    };
    // pre91: Insertable Streams nur im Caller-Modus (Gruppencall) + wenn der Browser es kann.
    if (callerRef.current && sframeSupported()) (pcConfig as any).encodedInsertableStreams = true;
    const pc = new RTCPeerConnection(pcConfig);
    pcsRef.current.set(from, pc);
    pc.ontrack = (e) => {
      // pre209: Audio & Video kommen beim Berater in UNTERSCHIEDLICHEN Streams (rawStream vs.
      // portrait.outputStream). Alle Tracks in EINEN Remote-Stream sammeln, sonst spielt das
      // Gast-Element nur den zuletzt gemeldeten Stream (oft Video ohne Audio → kein Ton).
      let stream = (pc as any).__remoteStream as MediaStream | undefined;
      if (!stream) { stream = new MediaStream(); (pc as any).__remoteStream = stream; }
      if (!stream.getTracks().some((t) => t.id === e.track.id)) stream.addTrack(e.track);
      // pre91: SFrame auf eingehenden Receiver (decrypt) — nur Caller-Modus.
      if (e2eeRef.current) applySframe(e.receiver, e2eeRef.current, 'decrypt');
      publishPeers();
    };
    // pre207: Kamera-Steuerkanal SELBST erzeugen (wie die Stele) — der Berater empfängt ihn
    // per ondatachannel. Der mobile Gast läuft über die Primary-PC (Berater = Answerer), wo
    // der Berater den Kanal NICHT erzeugt — deshalb muss der Gast es tun.
    try {
      const camCh = pc.createDataChannel(CAM_DATACHANNEL_LABEL, { ordered: true });
      camDcRef.current = camCh; // pre215
      const attach = () => {
        if (!camCtrlRef.current) return;
        attachGuestCamResponder(camCh, camCtrlRef.current, {
          onSignRequest: (bytes: ArrayBuffer, fn: string, sigId: string, signers?: Signer[], markers?: SignMarker[], requireIdent?: boolean, requireOtp?: boolean) => { if (signReqRef.current?.sigId === sigId) return; signReqRef.current = { bytes, name: fn, sigId }; setState((s) => ({ ...s, signRequest: { bytes, name: fn, sigId, signers, markers, requireIdent, requireOtp } })); },
          onLight: async (on: boolean) => {
            let ok = (await camCtrlRef.current?.setTorch(on)) ?? false;
            // Frontkamera hat keine Taschenlampe → auf Rückkamera wechseln und erneut versuchen.
            if (!ok && on && facingRef.current !== 'environment' && applyCameraRef.current) {
              await applyCameraRef.current('environment');
              ok = (await camCtrlRef.current?.setTorch(on)) ?? false;
            }
            return ok;
          },
        });
      };
      camCh.onopen = attach;
      attach();
    } catch (err) { console.warn('[guest-rtc] cam-dc create fehlgeschlagen', err); }
    // Fallback: falls die Gegenseite den Kanal erzeugt.
    pc.ondatachannel = (e) => {
      if (e.channel.label === CAM_DATACHANNEL_LABEL && camCtrlRef.current) { camDcRef.current = e.channel; attachGuestCamResponder(e.channel, camCtrlRef.current, {
        onSignRequest: (bytes: ArrayBuffer, fn: string, sigId: string, signers?: Signer[], markers?: SignMarker[], requireIdent?: boolean, requireOtp?: boolean) => { if (signReqRef.current?.sigId === sigId) return; signReqRef.current = { bytes, name: fn, sigId }; setState((s) => ({ ...s, signRequest: { bytes, name: fn, sigId, signers, markers, requireIdent, requireOtp } })); },
      }); }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) socketRef.current?.emit('rtc:ice', { candidate: e.candidate, to: from });
    };
    pc.onconnectionstatechange = () => {
      console.log('[guest-rtc] pc', from, pc.connectionState);
      if (pc.connectionState === 'connected') { wasConnectedRef.current = true; setState((s) => ({ ...s, status: 'connected' })); }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        pcsRef.current.delete(from);
        publishPeers();
      }
    };
    return pc;
  }, [publishPeers]);

  const addLocalTracks = useCallback((pc: RTCPeerConnection) => {
    const local = localStreamRef.current;
    if (!local || (pc as any).__localAdded) return;
    for (const track of local.getTracks()) pc.addTrack(track, local);
    (pc as any).__localAdded = true;
  }, []);

  const flushIce = useCallback(async (pc: RTCPeerConnection, from: string) => {
    const buffered = pendingIceRef.current.get(from) || [];
    for (const c of buffered) await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
    pendingIceRef.current.delete(from);
  }, []);

  // ── Eingehendes Offer → antworten (Answerer-Pfad) ─────────────────────────
  const handleOffer = useCallback(
    async (from: string, sdp: RTCSessionDescriptionInit) => {
      const socket = socketRef.current;
      const local = localStreamRef.current;
      if (!socket || !local) return;
      const pc = ensurePc(from);
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      // Lokale Kamera + Mikro ERST NACH setRemoteDescription anhängen → die Tracks
      // werden den vom Offer angelegten Transceivern korrekt zugeordnet.
      addLocalTracks(pc);
      // pre213: VP9 nur wenn E2EE tatsächlich aktiv (Call-Intent + SFrame-fähig), sonst H264.
      const useVp9 = e2eeIntentRef.current && !!e2eeRef.current;
      preferVideoCodec(pc, useVp9 ? 'VP9' : 'H264');
      await flushIce(pc, from);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription({ type: 'answer', sdp: useVp9 ? (answer.sdp || '') : preferH264InSdp(answer.sdp || '') });
      void tuneVideoSender(pc);
      socket.emit('rtc:answer', { sdp: pc.localDescription, to: from });
    },
    [ensurePc, addLocalTracks, flushIce],
  );

  // pre91: eigenen ECDH-Public-Key an den Peer senden (nur Caller + Intent).
  const sendE2eeKey = useCallback(async (to: string) => {
    const sess = e2eeRef.current;
    if (!sess || !e2eeIntentRef.current) return;
    try {
      const pub = await sess.exportPublicKey();
      socketRef.current?.emit('rtc:e2ee-key', { publicKey: pub, keyId: sess.keyId, to });
      console.log('[guest-rtc] E2EE Public Key gesendet');
    } catch (err) {
      console.warn('[guest-rtc] E2EE Public Key senden fehlgeschlagen', err);
    }
  }, []);

  // ── pre90: Caller-Pfad — Gast (Gruppenlink) offert wie eine Stele ─────────
  const makeOffer = useCallback(
    async (to: string) => {
      const socket = socketRef.current;
      const local = localStreamRef.current;
      if (!socket || !local) return;
      if (pcsRef.current.has(to)) return; // bereits am Aushandeln/verbunden
      const pc = ensurePc(to);
      addLocalTracks(pc); // beim Offerer VOR createOffer
      // pre91: SFrame auf ausgehende Sender (encrypt) — nur Caller-Modus.
      if (e2eeRef.current) {
        for (const sender of pc.getSenders()) {
          if (sender.track) applySframe(sender, e2eeRef.current, 'encrypt');
        }
      }
      // pre213: Wenn dieser Gast tatsächlich E2EE fährt (Call-Intent + SFrame-fähig, z. B.
      // Desktop-Chrome), VP9 bevorzugen (wie die Stele). Sonst H264 (iOS/Hardware).
      const useVp9 = e2eeIntentRef.current && !!e2eeRef.current;
      if (useVp9) preferVideoCodec(pc, 'VP9');
      const offer = await pc.createOffer();
      const offerSdp = useVp9 ? (offer.sdp || '') : preferH264InSdp(offer.sdp || '');
      await pc.setLocalDescription({ type: 'offer', sdp: offerSdp });
      socket.emit('rtc:offer', { sdp: pc.localDescription, to });
      void sendE2eeKey(to);
    },
    [ensurePc, addLocalTracks, sendE2eeKey],
  );

  const handleAnswer = useCallback(
    async (from: string, sdp: RTCSessionDescriptionInit) => {
      const pc = pcsRef.current.get(from);
      if (!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(sdp)).catch((e) => console.warn('[guest-rtc] setRemote(answer)', e));
      await flushIce(pc, from);
    },
    [flushIce],
  );

  useEffect(() => {
    if (!rtcToken) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'connecting' }));

    (async () => {
      // Lokale Medien
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        localStreamRef.current = stream;
        cameraTrackRef.current = stream.getVideoTracks()[0] || null;
        setState((s) => ({ ...s, localStream: stream }));
        // pre204: mobiler Kamera-Responder (Scan/Zoom/Licht/Kamerawechsel).
        facingRef.current = 'user';
        if (cameraTrackRef.current) {
          const onOutputTrack = (track: MediaStreamTrack) => {
            for (const pc of pcsRef.current.values()) {
              const sender = pc.getSenders().find((se) => se.track && se.track.kind === 'video');
              if (sender && sender.track !== track) sender.replaceTrack(track).catch(() => { /* */ });
            }
          };
          camCtrlRef.current = new GuestCamController(cameraTrackRef.current, onOutputTrack);
        }
      } catch (e) {
        setState((s) => ({ ...s, status: 'error', error: 'Kamera/Mikrofon nicht verfügbar oder nicht freigegeben.' }));
        return;
      }

      // ICE-Server best effort holen (gleiche Quelle wie die anderen Clients)
      (window as any).__iceServers = await loadIceServers();

      // pre91: SFrame-Session nur im Caller-Modus (Gruppencall) — verschlüsselt wird erst,
      // wenn der Intent (Tenant-Policy via rtc:joined) es will und der Peer bereit ist.
      e2eeRef.current = null;
      if (callerRef.current && sframeSupported()) {
        try {
          e2eeRef.current = await createE2eeSession();
          console.log('[guest-rtc] SFrame-Session bereit (Caller-Modus)');
        } catch (err) {
          console.warn('[guest-rtc] E2EE-Setup fehlgeschlagen, fahre ohne SFrame fort', err);
          e2eeRef.current = null;
        }
      }

      const socket = io('/rtc', {
        auth: { token: rtcToken },
        transports: ['websocket'],
        reconnection: true,
      });
      socketRef.current = socket;

      socket.on('connect', () => console.log('[guest-rtc] connected', socket.id));
      // pre209: Kamera auf ein bestimmtes facing umschalten (Front/Rück) — von rtc:swap UND
      // vom Licht (Torch braucht die Rückkamera) genutzt.
      applyCameraRef.current = async (next: 'user' | 'environment') => {
        if (facingRef.current === next && cameraTrackRef.current) return;
        try {
          const ns = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: next }, width: { ideal: 1280 }, height: { ideal: 720 } } });
          const nt = ns.getVideoTracks()[0];
          if (!nt) return;
          const old = cameraTrackRef.current;
          facingRef.current = next;
          setState((s) => ({ ...s, cameraFacing: next }));
          cameraTrackRef.current = nt;
          camCtrlRef.current?.replaceSource(nt);
          const local = localStreamRef.current;
          if (local) {
            if (old) { try { local.removeTrack(old); } catch { /* */ } }
            local.addTrack(nt);
            const preview = new MediaStream([nt, ...local.getAudioTracks()]);
            setState((st) => ({ ...st, localStream: preview }));
          }
        } catch (err) {
          console.warn('[guest-rtc] Kamerawechsel fehlgeschlagen', err);
        }
      };
      // pre213: Kamera-Zyklus über die tatsächlichen Geräte (funktioniert auf Desktops mit
      // mehreren Webcams UND auf Mobilgeräten). Fällt auf Front/Rück zurück, wenn nur eine
      // Kamera sichtbar ist oder keine Geräte-IDs vorliegen.
      cycleCameraRef.current = async () => {
        try {
          const devs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
          if (devs.length < 2) {
            await applyCameraRef.current?.(facingRef.current === 'user' ? 'environment' : 'user');
            return;
          }
          const curId = (cameraTrackRef.current?.getSettings() as any)?.deviceId;
          const idx = Math.max(0, devs.findIndex((d) => d.deviceId === curId));
          const next = devs[(idx + 1) % devs.length];
          const ns = await navigator.mediaDevices.getUserMedia({ audio: false, video: { deviceId: { exact: next.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } } });
          const nt = ns.getVideoTracks()[0];
          if (!nt) return;
          const old = cameraTrackRef.current;
          cameraTrackRef.current = nt;
          // facing aus den Settings ableiten (relevant für die Torch-Logik)
          const fm = (nt.getSettings() as any)?.facingMode;
          if (fm === 'user' || fm === 'environment') { facingRef.current = fm; setState((s) => ({ ...s, cameraFacing: fm })); }
          camCtrlRef.current?.replaceSource(nt);
          const local = localStreamRef.current;
          if (local) {
            if (old) { try { local.removeTrack(old); } catch { /* */ } }
            local.addTrack(nt);
            setState((st) => ({ ...st, localStream: new MediaStream([nt, ...local.getAudioTracks()]) }));
          }
          console.log('[guest-rtc] Kamera gewechselt →', next.label || next.deviceId);
        } catch (err) {
          console.warn('[guest-rtc] Kamera-Zyklus fehlgeschlagen', err);
        }
      };
      // pre204/213: Berater tauscht die Kamera → nächstes Kameragerät (Front/Rück bzw. Webcam-Zyklus).
      socket.on('rtc:swap', () => { void cycleCameraRef.current?.(); });

      socket.on('rtc:joined', (data: { peers?: Array<{ peerId: string } & PeerMeta>; e2ee?: boolean }) => {
        console.log('[guest-rtc] joined', JSON.stringify(data));
        for (const p of data.peers || []) {
          if (p.peerId) metaRef.current.set(p.peerId, { name: p.name, role: p.role, guest: p.guest });
        }
        // pre91: E2EE-Intent server-autoritativ aus rtc:joined (nur relevant im Caller-Modus).
        e2eeIntentRef.current = e2eeForced() ?? Boolean(data.e2ee);
        // pre90: Als Anrufer (Gruppenlink) offern wir den bereits anwesenden Teilnehmern
        // (z. B. der Berater hat schon angenommen). Sonst (Experte) warten wir auf deren Offer.
        if (callerRef.current) {
          for (const p of data.peers || []) {
            if (p.peerId) void makeOffer(p.peerId);
          }
        }
      });

      socket.on('rtc:peer-joined', (data: { peerId?: string } & PeerMeta) => {
        if (data.peerId) metaRef.current.set(data.peerId, { name: data.name, role: data.role, guest: data.guest });
        // pre90: Anrufer offert dem neu beigetretenen Berater (makeOffer sendet auch den E2EE-Key).
        if (callerRef.current && data.peerId) void makeOffer(data.peerId);
      });

      socket.on('rtc:offer', (data: { sdp: any; from?: string }) => {
        if (!data.from) return;
        void handleOffer(data.from, data.sdp);
      });

      socket.on('rtc:answer', (data: { sdp: any; from?: string }) => {
        if (!data.from) return;
        void handleAnswer(data.from, data.sdp);
      });

      // pre91: SFrame-Schlüsselaustausch (nur Caller-Modus, gespiegelt zum Berater).
      const syncE2ee = () => setState((s) => ({ ...s, e2eeActive: e2eeRef.current?.isActive() ?? false }));
      socket.on('rtc:e2ee-key', async (data: { publicKey: string; keyId: number; from?: string }) => {
        if (!e2eeIntentRef.current || !data.from) return;
        console.log('[guest-rtc] E2EE Peer-Key empfangen');
        await e2eeRef.current?.onPeerKey(data.publicKey);
        if (e2eeRef.current?.hasKey()) socket.emit('rtc:e2ee-ready', { to: data.from });
        syncE2ee();
      });
      socket.on('rtc:e2ee-ready', () => {
        if (!e2eeIntentRef.current) return;
        e2eeRef.current?.markPeerReady();
        syncE2ee();
      });
      socket.on('rtc:e2ee-mode', (data: { active: boolean; participants: number }) => {
        console.log('[guest-rtc] E2EE-Modus:', data.active ? 'aktiv' : 'pausiert', `(${data.participants} Teilnehmer)`);
        e2eeRef.current?.setMode(data.active);
        syncE2ee();
      });

      socket.on('rtc:ice', async (data: { candidate: any; from?: string }) => {
        if (!data.from) return;
        const pc = pcsRef.current.get(data.from);
        if (pc && pc.remoteDescription) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
        } else {
          const list = pendingIceRef.current.get(data.from) || [];
          list.push(data.candidate);
          pendingIceRef.current.set(data.from, list);
        }
      });

      const dropPeer = (from?: string) => {
        if (!from) return;
        const pc = pcsRef.current.get(from);
        if (pc) {
          try { pc.close(); } catch { /* ignore */ }
          pcsRef.current.delete(from);
        }
        publishPeers();
      };
      socket.on('rtc:peer-left', (data: { peerId?: string }) => dropPeer(data.peerId));
      socket.on('rtc:bye', (data: { from?: string }) => {
        dropPeer(data.from);
        // War die Verbindung aufgebaut und ist jetzt niemand mehr da → Gespräch beendet.
        if (wasConnectedRef.current && pcsRef.current.size === 0) endCall('remote');
      });

      // Welle 8d/M2: Der Berater hat den Experten gezielt getrennt (ohne den Call zu beenden).
      socket.on('rtc:kicked', () => {
        endCall('remote');
      });

      // Welle 8d/M2: Server weist ab (z.B. bereits ein Experte zugeschaltet — Einzel-Slot).
      socket.on('rtc:rejected', (data: { reason?: string }) => {
        const msg = data?.reason === 'expert-busy'
          ? 'Es ist bereits ein Experte zugeschaltet. Aktuell ist nur ein Experte gleichzeitig möglich — bitte später erneut versuchen.'
          : 'Beitritt aktuell nicht möglich.';
        setState((s) => ({ ...s, status: 'error', error: msg }));
        try { socket.disconnect(); } catch { /* ignore */ }
      });
    })();

    return () => {
      cancelled = true;
      try { socketRef.current?.emit('rtc:bye'); } catch { /* ignore */ }
      try { socketRef.current?.disconnect(); } catch { /* ignore */ }
      socketRef.current = null;
      pcsRef.current.forEach((pc) => { try { pc.close(); } catch { /* ignore */ } });
      pcsRef.current.clear();
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      localStreamRef.current = null;
    };
  }, [rtcToken, handleOffer, publishPeers]);

  // Welle 8d/M2: iOS/iPadOS erlaubt nur EINEM Tab gleichzeitig Kamera/Mikrofon. Öffnet
  // der Gast einen neuen Experten-Link in einem neuen Tab, hält der alte (jetzt im
  // Hintergrund) die Geräte weiter belegt → der neue Tab bekommt kein Bild, bis der alte
  // geschlossen ist. Lösung: im Hintergrund die Geräte freigeben (Tracks stoppen) und beim
  // Zurückkehren neu holen + per replaceTrack wieder in die laufenden Strecken einhängen.
  useEffect(() => {
    const releaseCamera = () => {
      localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    };
    const reacquire = async () => {
      if (document.visibilityState !== 'visible') return;
      if (pcsRef.current.size === 0) return;          // nicht (mehr) im Call
      if (screenStreamRef.current) return;            // teilt gerade Bildschirm
      const cur = localStreamRef.current;
      const alive = cur && cur.getTracks().some((t) => t.readyState === 'live');
      if (alive) return;                              // Geräte laufen noch
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        });
        localStreamRef.current = stream;
        cameraTrackRef.current = stream.getVideoTracks()[0] || null;
        const vt = stream.getVideoTracks()[0] || null;
        const at = stream.getAudioTracks()[0] || null;
        setState((s) => {
          if (vt) vt.enabled = s.camOn;
          if (at) at.enabled = s.micOn;
          return { ...s, localStream: stream };
        });
        pcsRef.current.forEach((pc) => {
          pc.getSenders().forEach((sn) => {
            if (sn.track?.kind === 'video' && vt) void sn.replaceTrack(vt);
            if (sn.track?.kind === 'audio' && at) void sn.replaceTrack(at);
          });
        });
        console.log('[guest] Kamera/Mikro nach Rückkehr neu geholt');
      } catch { /* Nutzer hat verweigert o.ä. */ }
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') releaseCamera();
      else void reacquire();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', releaseCamera);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', releaseCamera);
    };
  }, []);

  const toggleMic = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setState((s) => ({ ...s, micOn: track.enabled }));
    }
  }, []);

  const toggleCam = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setState((s) => ({ ...s, camOn: track.enabled }));
    }
  }, []);

  // Bildschirm teilen: Video-Sender aller Strecken auf den Screen-Track umstellen
  // (replaceTrack → keine Neuverhandlung). Erneuter Aufruf schaltet zurück.
  const toggleScreen = useCallback(async () => {
    if (state.screenSharing) {
      const cam = cameraTrackRef.current;
      pcsRef.current.forEach((pc) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender && cam) void sender.replaceTrack(cam);
      });
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
      setState((s) => ({ ...s, screenSharing: false }));
      return;
    }
    // pre212: getDisplayMedia gibt es auf iOS/iPadOS nicht. Wir versuchen es trotzdem
    // (empirisch) und geben bei Nichtverfügbarkeit/Fehler eine klare Rückmeldung.
    if (typeof navigator.mediaDevices?.getDisplayMedia !== 'function') {
      setState((s) => ({ ...s, screenShareError: 'Bildschirmfreigabe wird von diesem Browser nicht unterstützt (auf iPhone/iPad systembedingt nicht möglich).' }));
      return;
    }
    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = screen.getVideoTracks()[0];
      screenStreamRef.current = screen;
      pcsRef.current.forEach((pc) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) void sender.replaceTrack(screenTrack);
      });
      screenTrack.onended = () => { void toggleScreen(); };
      setState((s) => ({ ...s, screenSharing: true, screenShareError: null }));
    } catch (e: any) {
      // NotAllowedError = Nutzer hat abgebrochen (kein Fehler-Banner nötig); alles andere melden.
      const aborted = e?.name === 'NotAllowedError' || e?.name === 'AbortError';
      setState((s) => ({ ...s, screenShareError: aborted ? null : `Bildschirmfreigabe nicht möglich: ${e?.name || 'Fehler'}` }));
    }
  }, [state.screenSharing]);

  // Welle 8d/M2: Verbindung beenden + verabschieden. 'self' = Experte legt auf,
  // 'remote' = Berater trennt / Call endet. Sendet bye (kaskadiert zur Stele), gibt
  // Kamera/Mikro frei und schaltet die UI auf den Verabschiedungs-Screen.
  const endCall = useCallback((reason: 'self' | 'remote') => {
    try { socketRef.current?.emit('rtc:bye'); } catch { /* ignore */ }
    try { socketRef.current?.disconnect(); } catch { /* ignore */ }
    pcsRef.current.forEach((pc) => { try { pc.close(); } catch { /* ignore */ } });
    pcsRef.current.clear();
    screenStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    localStreamRef.current = null;
    setState((s) => ({ ...s, status: 'ended', endedReason: reason, peers: [], localStream: null }));
  }, []);

  const hangup = useCallback(() => endCall('self'), [endCall]);

  // getDisplayMedia ist auf iOS/iPadOS nicht verfügbar → Button ausblenden
  const screenShareSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function';

  // pre216: Während des Calls den Bildschirm wachhalten (Screen Wake Lock) und die Media
  // Session mit Metadaten versorgen — beides hilft, dass Video/Audio auf Mobilgeräten nicht
  // abdunkeln/pausieren. Wake Lock wird beim Sichtbarwerden erneut angefordert (iOS/Android
  // geben es beim Tab-Wechsel frei).
  useEffect(() => {
    if (state.status !== 'connected') return;
    const acquire = async () => {
      try { if ('wakeLock' in navigator && !wakeLockRef.current) wakeLockRef.current = await (navigator as any).wakeLock.request('screen'); }
      catch { /* Wake Lock nicht verfügbar/verweigert */ }
    };
    void acquire();
    const onVis = () => { if (!document.hidden) void acquire(); };
    document.addEventListener('visibilitychange', onVis);
    try {
      if ('mediaSession' in navigator) {
        const MM = (window as any).MediaMetadata;
        if (MM) (navigator as any).mediaSession.metadata = new MM({ title: 'Bamoa Videoberatung' });
        (navigator as any).mediaSession.playbackState = 'playing';
      }
    } catch { /* */ }
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      try { wakeLockRef.current?.release?.(); } catch { /* */ }
      wakeLockRef.current = null;
      try { if ('mediaSession' in navigator) (navigator as any).mediaSession.playbackState = 'none'; } catch { /* */ }
    };
  }, [state.status]);

  // pre215: Nativen Screenshot (oder ein Foto) aus der Fotos-App an den Berater senden.
  // Landet über die Scan-Pipeline im SnapshotViewer des Beraters — rein P2P, kein Server.
  const sendFile = useCallback(async (file: File): Promise<boolean> => {
    const dc = camDcRef.current;
    if (!dc || dc.readyState !== 'open') { console.warn('[guest-rtc] Dateiversand: Steuerkanal nicht offen'); return false; }
    try {
      const buf = await file.arrayBuffer();
      const mime = file.type || 'application/octet-stream';
      const format = mime.includes('png') ? 'png'
        : mime.includes('webp') ? 'webp'
        : mime.includes('pdf') ? 'pdf'
        : mime.startsWith('image/') ? 'jpeg' : 'file';
      await sendImageBytes(dc, buf, format, 'main', mime, file.name);
      return true;
    } catch (e) { console.warn('[guest-rtc] Dateiversand fehlgeschlagen', e); return false; }
  }, []);

  // pre219: signierte PDF-Bytes an den Berater zurückschicken (dort → Auto-Siegel).
  const submitSignature = useCallback(async (signed: Uint8Array, identProof?: unknown, identStatus?: string, otpCode?: string): Promise<boolean> => {
    const dc = camDcRef.current;
    const req = signReqRef.current;
    signReqRef.current = null;
    setState((s) => ({ ...s, signRequest: null }));
    if (!dc || dc.readyState !== 'open' || !req) return false;
    try { await sendSignResult(dc, req.sigId, req.name.replace(/\.pdf$/i, '') + '-signiert.pdf', signed.buffer as ArrayBuffer, identProof, identStatus, otpCode); return true; }
    catch (e) { console.warn('[guest-rtc] Signatur-Rücksendung fehlgeschlagen', e); return false; }
  }, []);
  const dismissSignature = useCallback(() => { signReqRef.current = null; setState((s) => ({ ...s, signRequest: null })); }, []);

  return { ...state, toggleMic, toggleCam, toggleScreen, sendFile, submitSignature, dismissSignature, hangup, screenShareSupported };
}
